import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput, formatFullPhone } from '@/components/ui/phone-input'
import { createClient, fetchClientsPage } from '@/lib/clientsApi'
import type { CalendarClient } from '@/types'

type ClientCreateDialogProps = {
  orgSlug: string
  initialName?: string
  onCreated: (client: CalendarClient) => void
  onClose: () => void
}

export function ClientCreateDialog({ orgSlug, initialName = '', onCreated, onClose }: ClientCreateDialogProps) {
  const [name, setName] = useState(initialName)
  const [countryCode, setCountryCode] = useState('+52')
  const [localPhone, setLocalPhone] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [whatsappOptedIn, setWhatsappOptedIn] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setSaving(true)
    setErrors({})
    const phone = formatFullPhone(countryCode, localPhone)
    try {
      const result = await createClient(orgSlug, {
        name: name.trim(),
        phone,
        email: email.trim() || undefined,
        notes: notes.trim() || undefined,
        whatsapp_opted_in: whatsappOptedIn
      })
      if (result.client) {
        onCreated({ id: result.client.id, name: result.client.name, phone: result.client.phone })
        return
      }

      const phoneError = result.errors?.phone || ''
      if (phoneError.toLowerCase().includes('ya existe')) {
        const digits = phone.replace(/\D/g, '')
        const existing = await fetchClientsPage(orgSlug, { page: 1, per: 5, q: digits })
        const match = existing.clients.find((client) => client.phone.replace(/\D/g, '').endsWith(digits.slice(-10)))
        if (match) {
          onCreated({ id: match.id, name: match.name, phone: match.phone })
          return
        }
      }
      setErrors(result.errors ?? { base: 'No se pudo crear el cliente' })
    } finally {
      setSaving(false)
    }
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-xl border border-border bg-card p-4 shadow-lg sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-semibold">Nuevo cliente</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-client-name">Nombre completo</Label>
            <Input
              id="new-client-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoFocus
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>
          <div className="space-y-2">
            <Label>Teléfono (WhatsApp)</Label>
            <PhoneInput
              countryCode={countryCode}
              localPhone={localPhone}
              onCountryCodeChange={setCountryCode}
              onLocalPhoneChange={setLocalPhone}
              required
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-client-email">Correo electrónico (opcional)</Label>
            <Input
              id="new-client-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-client-notes">Notas (opcional)</Label>
            <textarea
              id="new-client-notes"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input"
              checked={whatsappOptedIn}
              onChange={(event) => setWhatsappOptedIn(event.target.checked)}
            />
            Acepta recibir notificaciones por WhatsApp
          </label>
          {errors.base && <p className="text-sm text-destructive">{errors.base}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando...' : 'Crear cliente'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}
